/**
 * PersonHistoryBlock — B3 (spec 2026-08-12 §3.2): o histórico do cliente DENTRO
 * da Conversa em curso.
 *
 * Reusa o endpoint da ficha (`GET /v1/persons/:id/conversations`, capability
 * `person-read`) via `usePersonConversations` — mesmo cache React Query da
 * ficha, zero endpoint novo. Cada episódio: data + canal + status/desfecho +
 * excerto do LivingSummary; clique navega à conversa antiga.
 *
 * LIMITAÇÃO DECLARADA (spec §4): o endpoint lista metadados+resumo de TODAS as
 * conversas da pessoa, sem filtro adversarial (`persons.service.ts`
 * §listConversations, medido 12/Ago/2026) — exposição equivalente à do inbox.
 * Hardening para a vertical de mediação: frente B2.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCapability } from '../../../../hooks/use-capability';
import { api } from '../../../../lib/api-client';
import {
  channelLabel,
  conversationOutcomeLabel,
  conversationStatusLabel,
  formatShortDate,
} from '../../../persons/persons-adapter';
import { usePersonConversations } from '../../../persons/use-persons';

import type { ConversationDetail } from '../../../../lib/types';

export function PersonHistoryBlock({
  conversation,
}: {
  conversation: ConversationDetail;
}): JSX.Element | null {
  const navigate = useNavigate();
  const canViewPerson = useCapability('person-read');
  // D (spec §3.5) — a busca compartilha a família de exposição do transcript
  // contínuo (B2): mesma capability. O servidor segue autoritativo
  // (meta.enabled false ⇒ declara indisponível).
  const canSearch = useCapability('person-transcript-read');
  // Id ESCALAR primeiro (classe «vínculo mudo»: personId pode vir sem o ref).
  const personId = conversation.personId ?? conversation.person?.id ?? undefined;
  const enabled = canViewPerson && personId !== undefined;
  const { data, isLoading, isError } = usePersonConversations(personId, enabled);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const searchQuery = useQuery({
    queryKey: ['person-message-search', personId, searchTerm],
    enabled: canSearch && personId !== undefined && searchTerm.length >= 2,
    queryFn: () => api.searchPersonMessages(personId ?? '', { q: searchTerm }),
    staleTime: 30 * 1000,
  });

  if (!enabled) return null;
  if (isLoading) return <p className="placeholder-text">Carregando histórico…</p>;
  if (isError || data === undefined)
    // Falha declara indisponibilidade — nunca finge «sem conversas» (spec §5).
    return <p className="placeholder-text">Histórico indisponível no momento.</p>;

  const episodes = data.data.filter((c) => c.id !== conversation.id);
  if (episodes.length === 0)
    return <p className="placeholder-text">Nenhuma conversa anterior desta pessoa.</p>;

  const go = (id: string): void => {
    navigate(`/atendimento/conversation/${id}`);
  };

  return (
    <div>
      {/* D (spec §3.5) — «cadê o PIX?»: busca dentro das mensagens da pessoa. */}
      {canSearch && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSearchTerm(searchInput.trim());
          }}
          style={{ marginBottom: 6 }}
        >
          <input
            type="search"
            data-testid="person-history-search"
            className="input input--sm"
            style={{ width: '100%' }}
            placeholder="Buscar nas mensagens desta pessoa… (Enter)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </form>
      )}
      {searchTerm.length >= 2 && (
        <div style={{ marginBottom: 8 }} data-testid="person-history-search-results">
          {searchQuery.isLoading ? (
            <p className="placeholder-text">Buscando…</p>
          ) : searchQuery.isError || searchQuery.data === undefined ? (
            <p className="placeholder-text">Busca indisponível no momento.</p>
          ) : !searchQuery.data.meta.enabled ? (
            <p className="placeholder-text">Busca indisponível neste ambiente.</p>
          ) : searchQuery.data.data.length === 0 ? (
            <p className="placeholder-text">Nada encontrado para «{searchTerm}».</p>
          ) : (
            <ul className="person-mini-list">
              {searchQuery.data.data.map((r) => (
                <li
                  key={r.messageId}
                  className="person-mini-list__item"
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => go(r.conversationId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      go(r.conversationId);
                    }
                  }}
                >
                  <span className="person-mini-list__time">{formatShortDate(r.sentAt)}</span>
                  <div className="person-mini-list__body">
                    <div className="person-mini-list__excerpt">{r.excerpt}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ul className="person-mini-list">
        {episodes.map((c) => (
          <li
            key={c.id}
            className="person-mini-list__item"
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => go(c.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                go(c.id);
              }
            }}
          >
            <span className="person-mini-list__time">
              {formatShortDate(c.closedAt ?? c.updatedAt)}
            </span>
            <div className="person-mini-list__body">
              <div className="person-mini-list__title">Conversa · {channelLabel(c.channel)}</div>
              <div className="person-mini-list__sub mono">
                {conversationStatusLabel(c.status)}
                {c.outcomeType != null && ` · ${conversationOutcomeLabel(c.outcomeType)}`}
              </div>
              {/* Excerto narrativo (LivingSummary) — honest-defer: só quando existe. */}
              {c.excerpt != null && c.excerpt.length > 0 && (
                <div className="person-mini-list__excerpt">{c.excerpt}</div>
              )}
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        style={{ marginTop: 6 }}
        onClick={() => navigate(`/pessoas/${personId}#conversas`)}
      >
        {/* Contagem honesta: piso «N+» enquanto há mais páginas; N exato quando
            tudo carregado (revisão F.5 r2). */}
        Ver todas na ficha ({episodes.length.toString()}
        {data.meta.hasMore ? '+' : ''}) →
      </button>
    </div>
  );
}
